import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { Alert, Button, Checkbox, Form } from '@/shared/components';
import { PermissionGuard, usePermissions } from '@/features/auth';
import { isApiError, isNotFoundError } from '@/services/api';
import type { Product } from '@/features/products/types/products.types';
import { resolveProductCampaign } from '../services/campaign';
import { useCampaignCthfRules } from '../hooks/useCampaignCthfRules';
import { useConfigOptionsQuery } from '../hooks/useConfigOptionsQuery';
import { settingsToCthfForm, cthfFormToSettings, orderCapabilityOptions } from '../services/cthfRules';
import { cthfRulesSchema } from '../schemas/cthfRules.schema';
import type { CthfRulesFormValues } from '../schemas/cthfRules.schema';

interface Props {
  productId: string;
  product: Product;
}

const resolveCampaignKeys = {
  forProduct: (productId: string) => ['smr-admin', 'campaign', 'resolve', productId] as const,
};

const DEFAULT_VALUES: CthfRulesFormValues = {
  cthf_enabled: false,
  capabilities: {},
};

const CAPABILITIES_REGION_ID = 'cthf_capabilities';

/**
 * Bespoke CTHF Rules tab body. Mirrors CampaignBannerPanel's resolve-then-fetch two-query
 * structure, but the nine capability checkboxes are rendered DYNAMICALLY from the
 * money_handling catalogue (Decision D3) rather than being fixed fields — the capability
 * set is PROVISIONAL pending Finance (PRD 12 Q6-Q9), so a seeder re-run must change the
 * rendered form with no frontend edit.
 */
export function CampaignCTHFPanel({ productId, product }: Props) {
  const resolveQuery = useQuery({
    queryKey: resolveCampaignKeys.forProduct(productId),
    queryFn: () => resolveProductCampaign(productId),
  });

  const campaignId = resolveQuery.data?.campaign_id;

  const { query: rulesQuery, saveMutation } = useCampaignCthfRules(campaignId);

  // D4: per_page=100 — ConfigOptionController defaults to 10 and money_handling now holds
  // exactly 10 rows (9 capabilities + cthf_enabled), so the default would silently truncate
  // the group the moment Finance adds an eleventh capability.
  const catalogQuery = useConfigOptionsQuery({ group: 'money_handling', per_page: 100 });
  // Memoized so a stable [] (not a fresh array literal every render) reaches the
  // capabilities/capabilityKeys deps below while the query is in flight. The [] fallback is
  // for the hook layer ONLY — the seed effect below must still run against it so a stored
  // `cthf_enabled: true` is not lost. The RENDER path never treats it as a real answer:
  // catalogQuery.isLoading / isError get their own branches before the form, so loading,
  // failed and genuinely-empty catalogues stay three distinct states.
  const catalogOptions = useMemo(() => catalogQuery.data?.data ?? [], [catalogQuery.data]);
  const optionIdByKey = Object.fromEntries(catalogOptions.map((o) => [o.key_name, o.id]));

  const capabilities = useMemo(() => orderCapabilityOptions(catalogOptions), [catalogOptions]);
  const capabilityKeys = useMemo(() => capabilities.map((o) => o.key_name), [capabilities]);
  // Stable primitive for effect/memo deps — the array identity changes on every render.
  const capabilityKeysSignature = capabilityKeys.join('|');

  const isRetired = Boolean(product.deleted_at);

  // The form itself is NOT wrapped in PermissionGuard, so a user with only campaign.read
  // still sees the current CTHF posture (matches the banner/transfer panel convention).
  // Individual controls are disabled for that case; only the Save action is additionally
  // gated by PermissionGuard below. This is a UX convenience — the real authority is the
  // backend's permission:campaign.edit + audit middleware on the POST attach endpoint.
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission({ resource: 'campaign', action: 'edit' });
  const controlsDisabled = isRetired || !canEdit;

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { isDirty, isSubmitting },
  } = useForm<CthfRulesFormValues>({
    resolver: zodResolver(cthfRulesSchema),
    defaultValues: DEFAULT_VALUES,
  });

  // Seed from settings AND the live capability list. The capability deps matter: if the
  // catalogue resolves AFTER the settings query, a settings-only dep would leave the record
  // seeded from an empty key set and every checkbox stuck unticked; the signature dep makes
  // the effect re-run and re-seed the moment the catalogue lands. There is deliberately NO
  // `capabilityKeys.length === 0` early-return: on an unseeded/empty catalogue a stored
  // `cthf_enabled: true` must still seed the master toggle, otherwise the panel would render
  // it unchecked and a Save would silently overwrite the stored `true` with `false`. The
  // isDirty guard is preserved so a background refetch cannot clobber an in-progress edit;
  // post-save re-seeding is handled explicitly in onSubmit (D8).
  useEffect(() => {
    if (!rulesQuery.data) return;
    if (isDirty) return;
    reset(settingsToCthfForm(rulesQuery.data, capabilityKeys));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rulesQuery.data, capabilityKeysSignature]);

  const enabled = watch('cthf_enabled');

  const onSubmit = (values: CthfRulesFormValues) => {
    saveMutation.mutate(cthfFormToSettings(values, optionIdByKey, capabilityKeys), {
      // Re-seed from the server-normalized response so the form is not left dirty and the
      // background-refetch guard above can re-seed on subsequent refetches (PR #652 lesson).
      onSuccess: (saved) => reset(settingsToCthfForm(saved, capabilityKeys)),
    });
  };

  if (resolveQuery.isLoading) {
    return <p className="text-sm text-mp-muted">Loading money-handling capabilities…</p>;
  }

  const noCampaignAlert = (
    <Alert variant="warning">
      <span>
        This product has no campaign yet. Money-handling capabilities are unavailable until one is provisioned.
      </span>
    </Alert>
  );

  // The product->campaign resolver returns a 404 for an unprovisioned product, so a real
  // "no campaign yet" case surfaces as a 404-shaped ApiError here, not as a 200 with an
  // empty campaign_id. Handle it before the generic error branch so the friendly warning
  // renders instead of the scary "Failed to load" alert. All other resolver errors still
  // fall through to the generic error alert below.
  if (isNotFoundError(resolveQuery.error)) {
    return noCampaignAlert;
  }

  if (resolveQuery.isError) {
    return (
      <Alert variant="error">
        <span>Failed to load this product&apos;s campaign. Money-handling capabilities are unavailable.</span>
      </Alert>
    );
  }

  // Belt-and-braces: also cover a 200-with-empty-campaign_id shape.
  if (!campaignId) {
    return noCampaignAlert;
  }

  if (rulesQuery.isLoading) {
    return <p className="text-sm text-mp-muted">Loading money-handling capabilities…</p>;
  }

  if (rulesQuery.isError) {
    return (
      <Alert variant="error">
        <span>Failed to load money-handling capabilities.</span>
      </Alert>
    );
  }

  // The catalogue gets the same explicit loading/error branches as the two queries above.
  // Without them an in-flight or failed catalogue is indistinguishable from a genuinely
  // empty one: a stored `cthf_enabled: true` would flash (or, on failure, permanently show)
  // "No money-handling capabilities are defined", and Save would stay live on a failed fetch
  // so an admin could persist the master row alone without ever seeing the capability rows.
  if (catalogQuery.isLoading) {
    return <p className="text-sm text-mp-muted">Loading the capability catalogue…</p>;
  }

  if (catalogQuery.isError) {
    return (
      <Alert variant="error">
        <span>
          Failed to load the money-handling capability catalogue. Money-handling capabilities are
          unavailable.
        </span>
      </Alert>
    );
  }

  const half = Math.ceil(capabilities.length / 2);
  const [leftColumn, rightColumn] = [capabilities.slice(0, half), capabilities.slice(half)];

  return (
    <Form onSubmit={handleSubmit(onSubmit)} className="space-y-6 max-w-3xl">
      <div>
        <Controller
          name="cthf_enabled"
          control={control}
          render={({ field }) => (
            <Checkbox
              id="cthf_enabled"
              label="CTHF Enabled"
              ariaLabel="CTHF Enabled"
              ariaControls={CAPABILITIES_REGION_ID}
              ariaExpanded={field.value}
              checked={field.value}
              onChange={field.onChange}
              disabled={controlsDisabled}
            />
          )}
        />
      </div>

      {enabled && capabilities.length === 0 && (
        <Alert variant="info">
          <span>No money-handling capabilities are defined in the catalogue.</span>
        </Alert>
      )}

      {enabled && capabilities.length > 0 && (
        <fieldset id={CAPABILITIES_REGION_ID} className="rounded-lg border border-mp-border p-4">
          <legend className="px-2 text-sm font-medium">Money-handling capabilities</legend>
          {/* Two explicit columns so DOM order == visual COLUMN-major reading order == tab
              order (see Decision D3). A `grid-cols-2` single list would fill row-wise and
              read across, not down; `grid-flow-col` would need a dynamic grid-rows-N class
              that Tailwind cannot generate at build time. */}
          <div className="grid gap-6 sm:grid-cols-2">
            {[leftColumn, rightColumn].map((column, i) => (
              <div key={i} className="space-y-3">
                {column.map((option) => (
                  <div>
                    <Controller
                      key={option.key_name}
                      name={`capabilities.${option.key_name}` as const}
                      control={control}
                      render={({ field }) => (
                        <Checkbox
                          id={`cthf_${option.key_name}`}
                          label={option.label ?? option.key_name}
                          ariaLabel={option.label ?? option.key_name}
                          checked={Boolean(field.value)}
                          onChange={field.onChange}
                          disabled={controlsDisabled}
                        />
                      )}
                    />
                  </div>
                ))}
              </div>
            ))}
          </div>
        </fieldset>
      )}

      {saveMutation.isError && (
        <Alert variant="error">
          <span>{isApiError(saveMutation.error) ? saveMutation.error.message : 'Failed to save CTHF rules.'}</span>
        </Alert>
      )}

      {!isRetired && (
        <PermissionGuard permission={{ resource: 'campaign', action: 'edit' }}>
          <Button size="sm" disabled={isSubmitting || saveMutation.isPending}>
            {saveMutation.isPending ? 'Saving…' : 'Save'}
          </Button>
        </PermissionGuard>
      )}
    </Form>
  );
}
